import { useMutation } from '@apollo/client';
import { Alert } from '@unmind/design-system';
import { tracking } from 'App/Tracking';
import { logException } from 'App/logging';
import {
  matchToPractitionersMutation as matchToPractitionersPayload,
  matchToPractitionersMutationVariables as matchToPractitionersVariables,
} from 'Services/BeGateway/Practitioner/__generated__/matchToPractitionersMutation';
import { MATCH_TO_PRACTITIONERS_MUTATION } from 'Services/BeGateway/Practitioner/practitioner.services';
import {
  countryRequiresState,
  useSaveUserLocation,
  useUserLocation,
} from 'Account/hooks';
import { Namespace } from 'i18next';
import React, { useReducer, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { BEGatewayQueryContext } from 'utils/apollo';
import { Form } from './components';
import {
  INITIAL_STATE,
  MatchingQuestionKeys,
  MatchingStateResponse,
  responseReducer,
} from './state';
import {
  AlertContainer,
  BackButton,
  BackIcon,
  BackText,
  DisclaimerTextContainer,
  QuestionCard,
  QuestionnaireContainer,
  QuestionnaireHeader,
} from './styles';

export function Questionnaire({ onSubmit }: { onSubmit(): void }) {
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [languageCount, setLanguageCount] = useState(1);
  const [formSubmissionError, setFormSubmissionError] = useState(false);
  const { userLocationData, hasValidLocationData } = useUserLocation();
  const [matchingQuestionState, setMatchingQuestionState] = useReducer(
    responseReducer,
    INITIAL_STATE,
  );

  useEffect(() => {
    if (hasValidLocationData) {
      setMatchingQuestionState({
        type: MatchingStateResponse[MatchingQuestionKeys.where_location],
        payload: {
          country: {
            value: userLocationData?.user?.country?.value ?? '',
            label: userLocationData?.user?.country?.value ?? '',
          },
          region: {
            value: userLocationData?.user?.state?.value ?? '',
            label: userLocationData?.user?.state?.value ?? '',
          },
        },
      });
    }
  }, [userLocationData, hasValidLocationData]);
  const { t } = useTranslation<Namespace<'book' | 'shared'>>([
    'book',
    'shared',
  ]);
  const { questionResponses } = matchingQuestionState;
  const { saveLocation, saving } = useSaveUserLocation(
    userLocationData?.user?.id,
  );

  const [submitMatchingQuestionnaire, { loading: submitMutationInFlight }] =
    useMutation<matchToPractitionersPayload, matchToPractitionersVariables>(
      MATCH_TO_PRACTITIONERS_MUTATION,
      BEGatewayQueryContext,
    );

  const handleQuestionnaireSubmit = async () => {
    const userCountry =
      questionResponses[MatchingQuestionKeys.where_location].country.value;
    const userRegion =
      questionResponses[MatchingQuestionKeys.where_location].region.value;

    const matchingInput = {
      sessionContext: questionResponses[MatchingQuestionKeys.sessions_for],
      location: {
        country: userCountry,
        region: countryRequiresState(userCountry) ? userRegion : null,
      },
      languages: questionResponses[MatchingQuestionKeys.what_language].map(
        l => l.value,
      ),
    };

    try {
      await submitMatchingQuestionnaire({
        variables: { input: matchingInput },
      });
      // Saved after matching: the cache update re-enables FindYourMatch's
      // matches query, which unmounts this form, so a failed match must come first.
      await saveLocation({ country: userCountry, state: userRegion });

      setFormSubmissionError(false);
      setMatchingQuestionState({
        type: MatchingStateResponse.reset_matching,
      });

      tracking.track('talk-matching-completed');
      onSubmit();
    } catch (e) {
      setFormSubmissionError(true);
      logException(e);
    }
  };

  return (
    <QuestionnaireContainer>
      <QuestionnaireHeader>
        {currentQuestion >= 1 && (
          <BackButton onClick={() => setCurrentQuestion(currentQuestion - 1)}>
            <BackIcon />
            <BackText>
              {t('book:therapyAndCoaching.matches.questionnaire.ctas.previous')}
            </BackText>
          </BackButton>
        )}
      </QuestionnaireHeader>
      <QuestionCard>
        <Form
          currentQuestion={currentQuestion}
          setCurrentQuestion={setCurrentQuestion}
          questionResponses={questionResponses}
          setMatchingQuestionState={setMatchingQuestionState}
          addedLanguagesCount={languageCount}
          setLanguageCount={setLanguageCount}
          onQuestionnaireSubmit={handleQuestionnaireSubmit}
          questionnaireSubmissionInFlight={submitMutationInFlight || saving}
        />
        {formSubmissionError ? (
          <AlertContainer>
            <Alert
              variant="error"
              size="small"
              title={t('shared:errors.messages.something_wrong')}
            />
          </AlertContainer>
        ) : null}
      </QuestionCard>
      <DisclaimerTextContainer>
        {t('book:therapyAndCoaching.matches.questionnaire.disclaimer')}
      </DisclaimerTextContainer>
    </QuestionnaireContainer>
  );
}

export default Questionnaire;
